import * as userApi from "../api"
import { useSnackbar } from "notistack"
import { NotificationChannel, NotificationType } from "../../notifications/notifications"
import { useI18n } from "../../hooks"
import { unknownErrorToString } from "../../utils/errors"

export const useUserMutations = () => {
  const translations = useTranslations()
  const { enqueueSnackbar } = useSnackbar()
  const [updateNotificationSettingsMutation] = userApi.useUpdateNotificationSettingsMutation()
  const [updateUserInfoMutation] = userApi.useUpdateUserInfoMutation()
  const [updateUserEmailMutation] = userApi.useUpdateUserEmailMutation()
  const [verifyEmailMutation] = userApi.useVerifyEmailMutation()
  const [resendOtpMutation] = userApi.useResendOtpMutation()
  const [cancelEmailChangeRequestMutation] = userApi.useCancelEmailChangeRequestMutation()
  const [updateUserPasswordMutation] = userApi.useUpdateUserPasswordMutation()

  const showSuccessMessage = (message: string) => {
    enqueueSnackbar(message, { variant: "success" })
  }

  const handleError = (error: any) => {
    const errorMessage = unknownErrorToString(error)
    enqueueSnackbar(errorMessage, { variant: "error" })
  }

  const enableNotification = (userId: string, type: NotificationType, channel: NotificationChannel) => {
    const updateData = { enable: [{ type, channel }], disable: [] }
    return updateNotificationSettings(userId, updateData)
  }

  const disableNotification = (userId: string, type: NotificationType, channel: NotificationChannel) => {
    const updateData = { enable: [], disable: [{ type, channel }] }
    return updateNotificationSettings(userId, updateData)
  }

  const updateNotificationSettings = async (userId: string, data: userApi.UserNotificationSettingsUpdateData) => {
    return await updateNotificationSettingsMutation({ userId, ...data })
      .unwrap()
      .catch(handleError)
  }

  const updateUserInfo = async (userId: string, data: userApi.UserInfoUpdateData) => {
    return await updateUserInfoMutation({ userId, ...data })
      .unwrap()
      .catch(handleError)
  }

  const updateUserEmail = async (userId: string, data: userApi.UserEmailUpdateData) => {
    return await updateUserEmailMutation({ userId, ...data })
      .unwrap()
      .catch(handleError)
  }

  const verifyEmail = async (userId: string, data: userApi.UserEmailVerificationData) => {
    return await verifyEmailMutation({ userId, ...data })
      .unwrap()
      .then(() => showSuccessMessage(translations.updateEmailSuccessMessage))
      .catch(handleError)
  }

  const resendOtp = async (userId: string) => {
    return await resendOtpMutation({ userId })
      .unwrap()
      .then(() => showSuccessMessage(translations.resendOtpSuccessMessage))
      .catch(handleError)
  }

  const cancelEmailChangeRequest = async (userId: string) => {
    return await cancelEmailChangeRequestMutation({ userId })
      .unwrap()
      .then(() => showSuccessMessage(translations.cancelEmailChangeRequestSuccessMessage))
      .catch(handleError)
  }

  const updateUserPassword = async (userId: string, data: userApi.UserPasswordUpdateData) => {
    return await updateUserPasswordMutation({ userId, ...data })
      .unwrap()
      .then(() => showSuccessMessage(translations.updatePasswordSuccessMessage))
      .catch(handleError)
  }

  return {
    enableNotification,
    disableNotification,
    updateUserInfo,
    updateUserEmail,
    updateUserPassword,
    updateNotificationSettings,
    verifyEmail,
    resendOtp,
    cancelEmailChangeRequest,
  }
}

const useTranslations = (defaults = defaultTranslations): Translations => {
  const { translations = {} } = useI18n()

  const {
    updateEmailSuccessMessage = defaults.updateEmailSuccessMessage,
    updatePasswordSuccessMessage = defaults.updatePasswordSuccessMessage,
    resendOtpSuccessMessage = defaults.resendOtpSuccessMessage,
    cancelEmailChangeRequestSuccessMessage = defaults.cancelEmailChangeRequestSuccessMessage,
  } = translations

  return {
    updateEmailSuccessMessage,
    updatePasswordSuccessMessage,
    resendOtpSuccessMessage,
    cancelEmailChangeRequestSuccessMessage,
  }
}

const defaultTranslations = {
  updateEmailSuccessMessage: "Email updated.",
  updatePasswordSuccessMessage: "Password updated.",
  resendOtpSuccessMessage: "Verification code sent.",
  cancelEmailChangeRequestSuccessMessage: "Email change request cancelled.",
}

type Translations = typeof defaultTranslations
